import { z } from 'zod';
import { PlatformError } from './errors.ts';

/**
 * 17a (ADR-015): activity and activation identity. Kept apart from `process.ts` so `task.ts`, `case.ts` and
 * `inbound.ts` can reference activations without an import cycle.
 */

/** Activity or gate id declared by a process: lowercase, no `#` (reserved for iterations), no `/` or `:`. */
export const ActivityId = z
  .string()
  .regex(/^[a-z][a-z0-9_.-]{0,62}$/, 'activity id must be lowercase [a-z0-9_.-], max 63 chars')
  .meta({ id: 'ActivityId' });
export type ActivityId = z.infer<typeof ActivityId>;

export const GateId = ActivityId.meta({ id: 'GateId' });
export type GateId = z.infer<typeof GateId>;

// -----------------------------------------------------------------------------------------------
// Activation identity
// -----------------------------------------------------------------------------------------------

/**
 * One activation of an activity: `<activityId>#<iteration>`, iteration ≥ 1. A revision or loop re-entry gets the
 * next iteration; a retry of the same activation keeps it (retry ≠ revision). Engine step names for the activation
 * derive from it (`activationStepName`), so re-entry never replays a previous iteration's memoized result.
 */
export const ActivationId = z
  .string()
  .regex(/^[a-z][a-z0-9_.-]{0,62}#[1-9][0-9]{0,8}$/, 'activation id must be <activityId>#<iteration>')
  .meta({ id: 'ActivationId' });
export type ActivationId = z.infer<typeof ActivationId>;

/** Builds `<activityId>#<iteration>`. Throws `validation_failed` on a bad id or iteration < 1. */
export function makeActivationId(activityId: string, iteration: number): ActivationId {
  if (!Number.isInteger(iteration) || iteration < 1 || iteration > 999_999_999) {
    throw new PlatformError(
      'validation_failed',
      `Activation iteration must be an integer ≥ 1, got ${iteration}`,
      {
        details: { activityId, iteration },
      },
    );
  }
  const parsed = ActivityId.safeParse(activityId);
  if (!parsed.success) {
    throw new PlatformError('validation_failed', `Invalid activity id "${activityId}"`, {
      details: { activityId, issues: parsed.error.issues },
    });
  }
  return `${activityId}#${iteration}`;
}

/** Splits an activation id. Throws `validation_failed` when it is not `<activityId>#<iteration>`. */
export function parseActivationId(activationId: string): { activityId: ActivityId; iteration: number } {
  if (!ActivationId.safeParse(activationId).success) {
    throw new PlatformError('validation_failed', `Invalid activation id "${activationId}"`, {
      details: { activationId },
    });
  }
  const hash = activationId.lastIndexOf('#');
  return { activityId: activationId.slice(0, hash), iteration: Number(activationId.slice(hash + 1)) };
}

/** Deterministic engine step name for `step` inside an activation, e.g. `approve-pr#2/requestApproval`. */
export function activationStepName(activationId: string, step: string): string {
  parseActivationId(activationId);
  if (step.length === 0 || step.length > 200) {
    throw new PlatformError('validation_failed', 'Step name must be 1–200 characters', {
      details: { activationId, step },
    });
  }
  return `${activationId}/${step}`;
}

/** Signal name that delivers a spawned task's completion to the parent run: `task:<activationId>`. */
export function taskSignalName(activationId: string): string {
  parseActivationId(activationId);
  return `task:${activationId}`;
}

/**
 * Idempotency key of the task spawned for an activation: `spawn:<processInstanceId>:<activationId>`, plus
 * `:a<attempt>` for a task-level retry (`attempt` > 0) of the same activation.
 */
export function spawnIdempotencyKey(processInstanceId: string, activationId: string, attempt = 0): string {
  parseActivationId(activationId);
  if (!Number.isInteger(attempt) || attempt < 0) {
    throw new PlatformError('validation_failed', `Spawn attempt must be an integer ≥ 0, got ${attempt}`, {
      details: { activationId, attempt },
    });
  }
  const base = `spawn:${processInstanceId}:${activationId}`;
  return attempt === 0 ? base : `${base}:a${attempt}`;
}
